import React from "react";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTopContributors, fetchAskMeAnything, fetchLeaderboard } from "../../store/hallofFameSlice";
import { Box, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  HallofFameMainWrapper,
  BannerSection,
  BannerContent,
  BannerTitle,
  ContentWrapper,
  DescriptionText,
  SectionHeaderRow,
  SectionTitle,
  UpdatedText,
  CardsRow,
  ContributorCard,
  BadgeWrapper,
  AvatarWrapper,
  AvatarImage,
  ContributorBox,
  ContributorRoleSection,
  ContributorName,
  ContributorRole,
  Divider,
  StatsRow,
  StatItem,
  StatNumber,
  StatLabel,
  AskSectionWrapper,
  AskHeaderRow,
  HeaderIcon,
  AskTitle,
  AskSubtitle,
  NavButtons,
  ArrowButton,
  CarouselViewport,
  CarouselTrack,
  AskCard,
  AskCardHeader,
  AskCardBody,
  ProfileRow,
  SmallAvatar,
  ProfileName,
  ProfileRole,
  MetricRow,
  MetricNumber,
  SkillTags,
  Tag,
  Pagination,
  Dot,
  LeaderboardContent,
  LeaderboardHeader,
  LeaderboardImage,
  LeaderboardList,
  LeaderboardRow,
  LeaderboardSubtitle,
  LeaderboardTitle,
  LeaderboardWrapper,
  LeaderboardImageWrapper,
  MetricBlock,
  MetricSection,
  MetricValue,
  UserAvatar,
  UserName,
  UserSection,
  RankText,
} from "./HallofFame.styles";

function Contributor({
  badge,
  name,
  role,
  likes,
  answers,
  bestAnswers
}) {
  return (
    <ContributorCard>
      <BadgeWrapper>
        <img src={badge} alt="badge" height={25}/>
      </BadgeWrapper>
      <ContributorBox>
        <AvatarWrapper>
          <AvatarImage src={process.env.PUBLIC_URL + "/images/HallofFame/avatar_topcont.png"} alt={name} />
          {/*<StarIcon src={star} alt="star" />*/}
        </AvatarWrapper>
        <ContributorRoleSection>
        <ContributorName>{name}</ContributorName>
        <ContributorRole>{role}</ContributorRole>
        </ContributorRoleSection>
      </ContributorBox>
      
        <Divider/>
      <StatsRow>
        <StatItem>
          <StatNumber>{likes}</StatNumber>
          <StatLabel>Total likes</StatLabel>
        </StatItem>
        <StatItem>
          <StatNumber>{answers}</StatNumber>
          <StatLabel>Total answers</StatLabel>
        </StatItem>
        <StatItem>
          <StatNumber>{bestAnswers}</StatNumber>
          <StatLabel>Best answers</StatLabel>
        </StatItem>
      </StatsRow>
    </ContributorCard>
  );
}

const getTimeAgo = (timestamp) => {
  if (!timestamp) return "";

  const now = new Date();
  const updated = new Date(timestamp);
  const diff = Math.floor((now - updated) / 1000);

  if (diff < 60) return "Updated just now";
  if (diff < 3600) return `Updated ${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `Updated ${Math.floor(diff / 3600)} hours ago`;
  return `Updated ${Math.floor(diff / 86400)} days ago`;
};

function HallofFameContent() {

  const [loading, setLoading] = useState(true);

  const [startIndex, setStartIndex] = useState(0);
  const dispatch = useDispatch();
  const { topContributors, askMeAnything, leaderboard, topContributorsLastUpdated, leaderboardLastUpdated } = useSelector(
    (state) => state.hallOfFame
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          dispatch(fetchTopContributors()),
          dispatch(fetchAskMeAnything()),
          dispatch(fetchLeaderboard())
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [dispatch]);

  const cards = [
    {
      title:"People's choice contributor",
      icon: "/images/HallofFame/like-icon.png",
      metric: askMeAnything?.peoplesChoiceContributor?.metric || 0,
      label:"Likes",
      user: askMeAnything?.peoplesChoiceContributor
    },
    {
      title:"Constructive contributor",
      icon: "/images/HallofFame/active-icon.png",
      metric: askMeAnything?.constructiveContributor?.metric || 0,
      label:"Comments",
      user: askMeAnything?.constructiveContributor
    },
    {
      title:"Best influencer",
      icon: "/images/HallofFame/recognition-icon.png",
      metric: askMeAnything?.bestInfluencer?.metric || 0,
      label:"Best answers",
      user: askMeAnything?.bestInfluencer
    },
    {
      title:"Collaboration champion",
      icon: "/images/HallofFame/best-answer-icon.png",
      metric: askMeAnything?.collaborationChampion?.metric || 0,
      label:"Activities",
      user: askMeAnything?.collaborationChampion
    },
    {
      title:"Problem explorer",
      icon: "/images/HallofFame/active-icon.png",
      metric: askMeAnything?.problemExplorer?.metric || 0,
      label:"Questions",
      user: askMeAnything?.problemExplorer
    },
  ];

  const totalPages = cards.length;
  useEffect(() => {
    const interval = setInterval(() => {
      setStartIndex((prev) => (prev + 1) % cards.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [cards.length]);

  const loopCards = [...cards, ...cards];

  const slidePercent = 25.5;
  const offset = startIndex * slidePercent;

  const next = () => {
    setStartIndex((prev) => (prev + 1) % cards.length);
  };

  const prev = () => {
    setStartIndex((prev) => (prev - 1 + cards.length) % cards.length);
  };

  const isPrevDisabled = false;
  const isNextDisabled = false;

  return (
    <HallofFameMainWrapper>
    
      <BannerSection sx={{backgroundImage: `url(${process.env.PUBLIC_URL}/images/HallofFame/Hall-of-Fame_banner.jpg)`}}>
        <BannerContent>
          <BannerTitle>
            Hall of Fame
          </BannerTitle>
        </BannerContent>
      </BannerSection>

      {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "45vh",
            }}
          >
            <Typography sx={{fontSize: "20px"}}>Loading...</Typography>
          </Box>
        ) : (
          <>
            <ContentWrapper>
              <DescriptionText>
                The Hall of Fame celebrates the community members who consistently
                contribute high-quality knowledge and support learning across the
                portal. It recognizes top contributors based on expertise,
                engagement, and peer recognition. This space highlights role models
                who inspire others to learn, share, and grow together.
              </DescriptionText>

              <SectionHeaderRow>
                <SectionTitle>Top Contributors</SectionTitle>
                <UpdatedText>{getTimeAgo(topContributorsLastUpdated)}</UpdatedText>
              </SectionHeaderRow>

              <CardsRow>
                {topContributors.map((user, index) => (
                  <Contributor
                    key={user.id}
                    avatar="/images/avatar1.png"
                    badge={
                      index === 0
                        ? "/images/HallofFame/badge_topcont1.png"
                        : index === 1
                        ? "/images/HallofFame/badge_topcont2.png"
                        : "/images/HallofFame/badge_topcont3.png"
                    }
                    star={
                      index === 0
                        ? "/images/HallofFame/gold_star.png"
                        : index === 1
                        ? "/images/HallofFame/silver_star.png"
                        : "/images/HallofFame/bronze_star.png"
                    }
                    name={user.username}
                    role={user.designation}
                    likes={user.total_likes}
                    answers={user.answers_count}
                    bestAnswers={user.best_answers_count}
                  />
                ))}
              </CardsRow>
            </ContentWrapper>

            <AskSectionWrapper>
              <AskHeaderRow>
                <Box>
                  <AskTitle>COL Champions (Overall)</AskTitle>
                  <AskSubtitle>
                    Excellence in different areas of contribution
                  </AskSubtitle>
                </Box>

                <NavButtons>
                  <ArrowButton disabled={isPrevDisabled} onClick={prev}>
                    <ArrowBackIcon sx={{ fontSize: 20, color: isPrevDisabled ? "#9E9E9E" : "#FFFFFF", }} />
                  </ArrowButton>

                  <ArrowButton disabled={isNextDisabled} onClick={next}>
                    <ArrowForwardIcon sx={{ fontSize: 20, color: "#FFFFFF" }} />
                  </ArrowButton>
                </NavButtons>
              </AskHeaderRow>

              <CarouselViewport>
                <CarouselTrack offset={offset}>
                  {loopCards.map((card, i) => (
                    <AskCard key={i}>
                      <AskCardHeader>
                        <HeaderIcon src={card.icon} alt="" />
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily:"graphik" }}>
                          {card.title}
                        </Typography>
                      </AskCardHeader>

                      <AskCardBody>
                        <ProfileRow>
                          <SmallAvatar src="/images/avatar1.png" />
                          <Box>
                            <ProfileName>{card.user?.username}</ProfileName>
                            <ProfileRole>{card.user?.designation}</ProfileRole>
                          </Box>
                        </ProfileRow>

                        <MetricRow>
                          <MetricNumber>{card.metric}</MetricNumber>
                          <Typography style={{fontSize:"14px", fontFamily:"Graphik"}}>{card.label}</Typography>
                        </MetricRow>

                        <SkillTags>
                          {card.user?.skills?.length > 0 ? (
                            card.user.skills.map((skill, index) => (
                              <Tag key={index}>{skill}</Tag>
                            ))
                          ) : (
                            <Tag>-</Tag>
                          )}
                        </SkillTags>
                      </AskCardBody>
                    </AskCard>
                  ))}
                </CarouselTrack>
              </CarouselViewport>

              <Pagination>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <Dot key={i} active={i === startIndex} onClick={() => setStartIndex(i)} />
                ))}
              </Pagination>
            </AskSectionWrapper>

            <LeaderboardWrapper>
              <LeaderboardImageWrapper>
              <LeaderboardImage
                src="/images/HallofFame/leaderboard-img2.png"
                alt="Leaderboard"
              />
              </LeaderboardImageWrapper>
              {/* <LeaderboardImage /> */}
              <LeaderboardContent>
                <LeaderboardHeader>
                  <Box>
                    <LeaderboardTitle>Leaderboard</LeaderboardTitle>
                    <LeaderboardSubtitle>
                      Top-rated experts based on community contributions.
                    </LeaderboardSubtitle>
                  </Box>

                  <UpdatedText>{getTimeAgo(leaderboardLastUpdated)}</UpdatedText>
                </LeaderboardHeader>

                <LeaderboardList>
                  {leaderboard.map((user, index) => (
                    <LeaderboardRow key={user.id}>
                      <RankText>#{index + 1}</RankText>

                      <UserSection>
                        <UserAvatar src="/images/avatar1.png" />
                        <UserName>{user.username}</UserName>
                      </UserSection>

                      <MetricSection>
                        <MetricBlock>
                          <MetricValue>{user.total_points}</MetricValue>
                          <Typography sx={{ fontSize: "14px", fontFamily:"graphik" }}>
                            Points
                          </Typography>
                        </MetricBlock>
                      </MetricSection>
                    </LeaderboardRow>
                  ))}
                </LeaderboardList>
              </LeaderboardContent>
            </LeaderboardWrapper>
          </>
        )
      }
    </HallofFameMainWrapper>
  );
}

export default HallofFameContent;